/**
 * Zber diagnostických údajov pre hlásenia chýb.
 *
 * Drží kruhový buffer posledných JS chýb a neúspešných API volaní, aby sa dali
 * priložiť k tiketu. Buffer je čisto pasívny — nikam sám nič neposiela.
 *
 * DÔLEŽITÉ: nikdy nepriraďuj `window.onerror`. Robí to už
 * `features/errorBoundary.js` a druhé priradenie by jedno z nich umlčalo.
 * Preto sa tu používa výhradne `addEventListener`, ktorý s ním koexistuje.
 *
 * @module diagnostics
 */

const MAX_ENTRIES = 20;
const MAX_MESSAGE = 300;

/** Vlastné logovacie endpointy sa nikdy nezaznamenávajú — inak by zlyhanie
 *  odoslania tiketu vygenerovalo záznam, ktorý pôjde v ďalšom tikete, a tak dokola. */
const IGNORED_URL_PARTS = ["/api/support-tickets", "/api/frontend-errorlog"];

const buffer = [];
let initialized = false;

/** Logger nesmie nikdy vyhodiť chybu — inak by zhodil kód, ktorý ho volá. */
function push(entry) {
  try {
    buffer.push({ t: new Date().toISOString(), ...entry });
    while (buffer.length > MAX_ENTRIES) buffer.shift();
  } catch {
    /* ticho */
  }
}

const trim = (value) => String(value ?? "").slice(0, MAX_MESSAGE);

/**
 * Vráti z URL iba cestu, bez query stringu.
 * Query obsahuje e-maily, telefóny a mená zákazníkov — do diagnostiky nepatria.
 */
function safeUrl(url) {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return String(url ?? "").split("?")[0].slice(0, 200);
  }
}

/**
 * Zaznamená neúspešné API volanie. Volá sa z axios response interceptora.
 * Zámerne neukladá telo požiadavky ani hlavičky (token, osobné údaje).
 */
export function recordApiFailure({ method, url, status, message }) {
  if (IGNORED_URL_PARTS.some((part) => String(url ?? "").includes(part))) return;

  push({
    type: "API_ERROR",
    method: String(method ?? "").toUpperCase(),
    url: safeUrl(url),
    status: status ?? 0,
    message: trim(message),
  });
}

/** Aktuálny obsah kruhového buffera (kópia). */
export function getDiagnosticsLog() {
  return buffer.slice();
}

/** Vyprázdni buffer. Slúži predovšetkým pre testy. */
export function resetDiagnostics() {
  buffer.length = 0;
}

/**
 * Zostaví snapshot diagnostiky pre odoslanie s tiketom.
 * Volá sa až v momente odoslania, aby obsahoval čerstvý buffer.
 *
 * @param {object} extra - doplnkové údaje (napr. userId, role, screenshotError)
 */
export function buildDiagnostics(extra = {}) {
  const safe = (fn, fallback = null) => {
    try {
      return fn();
    } catch {
      return fallback;
    }
  };

  return {
    appVersion: process.env.REACT_APP_BUILD_VERSION || "neznáma",
    companyId: process.env.REACT_APP_COMPANY_ID || null,
    url: safe(() => window.location.href),
    userAgent: safe(() => navigator.userAgent),
    language: safe(() => navigator.language),
    online: safe(() => navigator.onLine),
    screen: safe(() => `${window.screen.width}x${window.screen.height}`),
    viewport: safe(() => `${window.innerWidth}x${window.innerHeight}`),
    devicePixelRatio: safe(() => window.devicePixelRatio),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    clientTime: new Date().toISOString(),
    ...extra,
    log: getDiagnosticsLog(),
  };
}

/**
 * Zaregistruje globálne listenery. Idempotentné — opakované volanie nič nerobí.
 * Volá sa raz z `features/axiosInterceptor.js` (ten je side-effect importovaný
 * v `index.js`).
 */
export function initDiagnostics() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (event) => {
    // Chyba načítania <img>/<script>/<link> — cieľom je element, nie okno.
    if (event?.target && event.target !== window && event.target.tagName) {
      push({
        type: "RESOURCE_ERROR",
        tag: event.target.tagName,
        url: safeUrl(event.target.src || event.target.href || ""),
      });
      return;
    }

    push({
      type: "JS_ERROR",
      message: trim(event?.message),
      source: safeUrl(event?.filename || ""),
      line: event?.lineno ?? null,
      stack: trim(event?.error?.stack),
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    push({
      type: "PROMISE_REJECTION",
      message: trim(reason instanceof Error ? reason.message : reason),
      stack: trim(reason?.stack),
    });
  });

  window.addEventListener("offline", () => push({ type: "OFFLINE" }));
  window.addEventListener("online", () => push({ type: "ONLINE" }));
}
